// Custom React hook for managing DataTrace transaction history
import { useState, useEffect, useCallback } from 'react';
import { 
  loadTransactionHistory, 
  saveTransaction as saveTransactionApi,
  updateTransactionStatus as updateTransactionStatusApi,
  checkTransactionStatus as checkTransactionStatusApi
} from '../services/datatraceTransactions';

export const useTransactionHistory = () => {
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [pollingTransactions, setPollingTransactions] = useState({});

  // Load history on mount
  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const transactions = await loadTransactionHistory();
      setTransactionHistory(transactions);
    } catch (error) {
      console.error('Failed to load transaction history:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const saveTransaction = useCallback(async (transaction) => {
    try {
      await saveTransactionApi(transaction);
      await loadHistory();
    } catch (error) {
      console.error('Failed to save transaction:', error);
      throw error;
    }
  }, [loadHistory]);

  const updateTransactionStatus = useCallback(async (transactionId, statusData) => {
    try {
      await updateTransactionStatusApi(transactionId, statusData);
      await loadHistory();
    } catch (error) {
      console.error('Failed to update transaction:', error);
      throw error;
    }
  }, [loadHistory]);

  const checkStatus = useCallback(async (transactionId) => {
    try {
      setPollingTransactions(prev => ({ ...prev, [transactionId]: 'loading' }));
      
      const statusResult = await checkTransactionStatusApi(transactionId);
      
      // Update persistent storage
      await updateTransactionStatus(transactionId, {
        status: statusResult.status,
        statusResponse: statusResult.fullResponse,
        lastStatusCheck: statusResult.rawResponse,
        lastChecked: statusResult.timestamp
      });

      setPollingTransactions(prev => ({ ...prev, [transactionId]: statusResult.status || 'checked' }));
      
      return statusResult;
    } catch (error) {
      setPollingTransactions(prev => ({ ...prev, [transactionId]: 'error' }));
      throw error;
    }
  }, [updateTransactionStatus]);

  const refreshHistory = loadHistory;

  return {
    transactionHistory,
    loadingHistory,
    pollingTransactions,
    loadHistory,
    saveTransaction,
    updateTransactionStatus,
    checkStatus,
    refreshHistory
  };
};

export default useTransactionHistory;
